import {noop, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import notifications from 'notifications';
import AbandonPlateMapConfirmationModal from 'samples/plate-maps/AbandonPlateMapConfirmationModal';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import DefinePlateMapStep from 'samples/plate-maps/annotate-fill-plate-modal/DefinePlateMapStep';
import FillPlateTransferStep from 'samples/plate-maps/fill-plate-modal/FillPlateTransferStep';
import {PlateModalMachineProvider} from 'samples/plate-maps/fill-plate-modal/PlateModalMachineProvider';
import {usePlateModalMachine} from 'samples/plate-maps/fill-plate-modal/usePlateModalMachine';
import {usePlateMapValidation} from 'samples/plate-maps/hooks/usePlateMapValidation';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {NoPlateMap} from 'samples/plate-maps/NoPlateMap';
import {
  DisplayLayerOptionSection,
  PlateMap,
  REAGENTS_ATTRIBUTE_SENTINEL,
  SchematizedDisplayLayerMetadata,
} from 'samples/plate-maps/PlateMapConstants';
import {plateMapHasAnyErrors} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {groupedValidationHasAnyBlockingErrors} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {ValidationErrorsByLayerAndLevel} from 'samples/plate-maps/validation/ValidationTypes';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Modal, {ModalButton} from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import StepNav, {StepItemStatus, StepNavSize, StepNavVariant} from 'ui-library/StepNav';
import {isServerRequestError} from 'util/Net';

import {FillPlateModalReducerActionType} from './machine/FillPlateModalConstants';

const MAX_PLATE_HEIGHT = 'calc(100vh - 400px)';

export interface FillPlateModalProps {
  initialPlateMap: PlateMap;
  selectedWellIndices: Array<number>;
  fillPlate: (callbackOnError: () => void, plateMap: PlateMap) => Promise<void>;
  eventGroupId: string;
  containerSchemaId: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export enum FillPlateModalStep {
  TRANSFER_SOURCES = 'TRANSFER_SOURCES',
  TRANSFER_QUANTITIES = 'TRANSFER_QUANTITIES',
}

const FILL_PLATE_MODAL_STEPS: Array<{key: FillPlateModalStep; title: string; metadata?: string}> = [
  {key: FillPlateModalStep.TRANSFER_SOURCES, title: 'Add sources'},
  {key: FillPlateModalStep.TRANSFER_QUANTITIES, title: 'Define transfer'},
];

// TODO: Get actual copy for these tooltips
const PROGRESS_DISABLED_TOOLTIP_BY_STEP: {[step in FillPlateModalStep]: string} = {
  [FillPlateModalStep.TRANSFER_SOURCES]: 'You must resolve all errors to proceed',
  [FillPlateModalStep.TRANSFER_QUANTITIES]: 'You must resolve all errors to proceed',
};

function getTooltipForUnresolvedValidationErrors({
  hasPlateMapStepErrors,
  hasTransferStepErrors,
  stepKey,
}: {
  hasPlateMapStepErrors: boolean;
  hasTransferStepErrors: boolean;
  stepKey: FillPlateModalStep;
}): string | null {
  if (stepKey === FillPlateModalStep.TRANSFER_SOURCES) {
    return PROGRESS_DISABLED_TOOLTIP_BY_STEP[stepKey];
  }
  if (hasPlateMapStepErrors) {
    return 'Previous step has errors, go back to resolve';
  } else if (hasTransferStepErrors) {
    return PROGRESS_DISABLED_TOOLTIP_BY_STEP[stepKey];
  }
  return null;
}

function getNextButtonTooltipTitle({
  cannotValidate,
  hasPlateMapStepErrors,
  hasTransferStepErrors,
  stepKey,
  validationStatus,
}: {
  cannotValidate: boolean;
  hasPlateMapStepErrors: boolean;
  hasTransferStepErrors: boolean;
  stepKey: FillPlateModalStep;
  validationStatus: AsyncStatus;
}): string | null {
  if (!cannotValidate) {
    if (validationStatus === AsyncStatus.SUCCESS) {
      return getTooltipForUnresolvedValidationErrors({
        hasPlateMapStepErrors,
        hasTransferStepErrors,
        stepKey,
      });
    } else if (validationStatus === AsyncStatus.NOT_FETCHED || validationStatus === AsyncStatus.LOADING) {
      return 'Validating...';
    } else {
      return 'Validation failed, please retry';
    }
  }
  return null;
}

function getModalButtons({
  stepKey,
  setStepKey,
  validatedPlateMap,
  validationErrors,
  validationStatus,
}: {
  stepKey: FillPlateModalStep;
  setStepKey: React.Dispatch<React.SetStateAction<FillPlateModalStep>>;
  validatedPlateMap: PlateMap | null;
  validationErrors: ValidationErrorsByLayerAndLevel | null;
  validationStatus: AsyncStatus;
}): Array<ModalButton> {
  const cannotValidate =
    validatedPlateMap == null || validationErrors == null || validationStatus !== AsyncStatus.SUCCESS;
  const hasPlateMapStepErrors = !cannotValidate && groupedValidationHasAnyBlockingErrors(validationErrors);
  const hasTransferStepErrors = !cannotValidate && plateMapHasAnyErrors(validatedPlateMap);

  let backButton: ModalButton;
  let nextButton: ModalButton;
  if (stepKey === FillPlateModalStep.TRANSFER_SOURCES) {
    backButton = {text: 'Cancel', cancel: true};
    nextButton = {
      text: 'Next',
      submit: true,
      style: ButtonStyle.PRIMARY,
      disabled: cannotValidate || hasPlateMapStepErrors,
      dataTestAttrs: {component: 'FillPlateModal', key: ModalButtonKey.NEXT},
      tooltipTitle: getNextButtonTooltipTitle({
        cannotValidate,
        hasPlateMapStepErrors,
        hasTransferStepErrors,
        stepKey,
        validationStatus,
      }),
    };
  } else {
    backButton = {
      text: 'Back',
      style: ButtonStyle.TERTIARY_GRAY,
      dataTestAttrs: {component: 'FillPlateModal', key: ModalButtonKey.BACK},
      onClick: () => {
        setStepKey(FillPlateModalStep.TRANSFER_SOURCES);
      },
    };
    nextButton = {
      text: 'Fill',
      submit: true,
      style: ButtonStyle.SECONDARY,
      disabled: cannotValidate || hasPlateMapStepErrors || hasTransferStepErrors,
      tooltipTitle: getNextButtonTooltipTitle({
        cannotValidate,
        hasPlateMapStepErrors,
        hasTransferStepErrors,
        stepKey,
        validationStatus,
      }),
      dataTestAttrs: {component: 'FillPlateModal', key: ModalButtonKey.NEXT},
    };
  }
  return [backButton, nextButton];
}

function getStatusByStepKey(newStepKey: FillPlateModalStep): {
  [key in FillPlateModalStep]: StepItemStatus;
} {
  const newStepIndex = FILL_PLATE_MODAL_STEPS.findIndex((step) => step.key === newStepKey);
  // @ts-expect-error objectFromEntries doesn't allow you to define the key type...
  return objectFromEntries(
    FILL_PLATE_MODAL_STEPS.map((step, index) => [
      step.key,
      index < newStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED,
    ])
  );
}

/**
 * Modal with the steps to fill a plate with entities.
 */
function ProvidedFillPlateModal(props: FillPlateModalProps): JSX.Element | null {
  const [stepKey, setStepKey] = useState<FillPlateModalStep>(FILL_PLATE_MODAL_STEPS[0].key);
  const [hasSeenTransferStep, setHasSeenTransferStep] = useState(false);
  const [statusByStepKey, setStatusByStepKey] = useState<{
    [key in FillPlateModalStep]: StepItemStatus;
  }>(getStatusByStepKey(stepKey));
  const modalRef = useRef<HTMLDivElement>(null);

  const [plateModalState, send] = usePlateModalMachine();
  const {plateMap, reagentGroupsById, pagination, viewMode} = plateModalState;

  const onLoadWellSchemaFields = useCallback((schemaFieldLayers: Array<SchematizedDisplayLayerMetadata>) => {
    send({
      type: FillPlateModalReducerActionType.SET_INITIAL_SELECTED_WELL_SCHEMA_FIELDS,
      schemaFieldLayers,
      initialSelectedLayerIds: null,
    });
    // We don't want this function handle to update. send _shouldn't_ change across renders - but better
    // safe than sorry
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wellSchemaLayers = useWellSchemaLayers(props.containerSchemaId, onLoadWellSchemaFields, []);

  const {refetch, validationStatus, validationRequestFailedMessage, validatedPlateMap, validationErrors} =
    usePlateMapValidation({
      plateId: plateMap?.id ?? null,
      plateMap,
      plateSchemaId: null,
      // This modal is only used in the inventory context, where there is no document or measurement layers
      documentId: null,
      includeMeasurementLayers: false,
    });

  const analyticsMetadata = {
    actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
    modalType: FixedPlateModalType.FILL_PLATE,
    eventGroupId: props.eventGroupId,
    plateId: props.initialPlateMap.id,
  };

  function onSetStepKey(newStepKey: FillPlateModalStep): void {
    analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.NAVIGATED_STEP_COMPONENT,
      ANALYTICS_MODULE.SAMPLES,
      {...analyticsMetadata, stepKey: newStepKey, previousStepKey: stepKey}
    );
    setStepKey(newStepKey);
    setStatusByStepKey(getStatusByStepKey(newStepKey));
    if (newStepKey === FillPlateModalStep.TRANSFER_QUANTITIES) {
      setHasSeenTransferStep(true);
    }
  }

  async function handleSubmit(): Promise<void> {
    if (stepKey === FillPlateModalStep.TRANSFER_SOURCES) {
      onSetStepKey(FillPlateModalStep.TRANSFER_QUANTITIES);
    } else {
      try {
        await props.fillPlate(refetch ?? noop, plateMap!);
        props.onRequestSubmit();
      } catch (e) {
        if (isServerRequestError(e)) {
          notifications.notifyError(e);
          return;
        }
        throw e;
      }
    }
  }

  return (
    <Modal
      title="Fill wells"
      onRequestClose={() => {
        if (!isEqual(plateMap, props.initialPlateMap)) {
          runModal(AbandonPlateMapConfirmationModal, {
            onRequestClose: noop,
            onRequestSubmit: props.onRequestClose,
            title: 'Leave plate fill?',
            confirmButtonText: 'Continue filling',
          });
        } else {
          props.onRequestClose();
        }
      }}
      onRequestSubmit={handleSubmit}
      fullWidth={true}
      expandHeight={true}
      buttons={getModalButtons({
        stepKey,
        setStepKey: onSetStepKey,
        validatedPlateMap,
        validationErrors,
        validationStatus,
      })}
      wrapInForm={false}
      closeableOnEscapeKey={false}
      ref={modalRef}
    >
      <StepNav
        currentStepKey={stepKey}
        onUpdateCurrentStep={onSetStepKey}
        steps={FILL_PLATE_MODAL_STEPS}
        statusByStep={statusByStepKey}
        size={StepNavSize.LARGE}
        variant={StepNavVariant.FLAT}
      />
      <hr className={css(styles.horizontalLine)} />
      {validatedPlateMap == null ? (
        <NoPlateMap
          refetch={refetch}
          validationStatus={validationStatus}
          validationRequestFailedMessage={validationRequestFailedMessage}
        />
      ) : (
        <>
          {stepKey === FillPlateModalStep.TRANSFER_SOURCES && (
            <DefinePlateMapStep
              plateMap={validatedPlateMap}
              setPlateMap={noop}
              initialPlateMap={props.initialPlateMap}
              initialSelectedWellIndices={plateModalState.selectedWellIndices}
              initialSelectedAttribute={REAGENTS_ATTRIBUTE_SENTINEL}
              modalRef={modalRef}
              analyticsMetadata={analyticsMetadata}
              maxPlateViewHeight={MAX_PLATE_HEIGHT}
              plateState={plateModalState}
              sendPlateModalMachineAction={send}
              disabledSections={[
                DisplayLayerOptionSection.WELL_ROLE,
                DisplayLayerOptionSection.SCHEMA_FIELDS,
                DisplayLayerOptionSection.PARAMETERS,
              ]}
              showFinalMeasurements={true}
              plateDataParameters={[]}
              procedureContext={{
                procedureId: null,
                isInProcedure: false,
                procedureStudyDesignType: null,
                procedureMethodExecutionInstanceId: null,
              }}
              selectedWellSchemaFieldIds={plateModalState.selectedWellSchemaFieldIds}
              wellSchemaLayers={wellSchemaLayers}
              onAddSelectedDataParameter={noop}
              onRemoveSelectedDataParameter={noop}
              onAddSelectedSchemaField={noop}
              onRemoveSelectedSchemaField={noop}
              initialShouldPreviewWellRoles={
                hasSeenTransferStep ? false : props.initialPlateMap.wells.some((well) => well.role != null)
              }
              validationErrors={validationErrors}
              onDismissErrors={noop}
              plateMapType={PlateMapType.RECORD}
            />
          )}
          {stepKey === FillPlateModalStep.TRANSFER_QUANTITIES && (
            <FillPlateTransferStep
              sendPlateModalMachineAction={send}
              pagination={pagination}
              plateMap={validatedPlateMap}
              reagentGroupsById={reagentGroupsById}
              selectedWellIndices={plateModalState.selectedWellIndices}
              viewMode={viewMode}
              modalRef={modalRef}
              analyticsMetadata={analyticsMetadata}
              maxPlateViewHeight={MAX_PLATE_HEIGHT}
              procedureStudyDesignType={null}
              containerSchemaId={props.containerSchemaId}
              plateMapType={PlateMapType.RECORD}
            />
          )}
        </>
      )}
    </Modal>
  );
}

/**
 * Wrapper component that provides the FillPlateModal machine context
 */
export default function FillPlateModal(props: FillPlateModalProps): JSX.Element | null {
  return (
    <PlateModalMachineProvider
      initialState={{
        plateMap: props.initialPlateMap,
        previewPlateMap: props.initialPlateMap,
        selectedWellIndices: new Set(props.selectedWellIndices),
      }}
    >
      <ProvidedFillPlateModal {...props} />
    </PlateModalMachineProvider>
  );
}

const styles = StyleSheet.create({
  horizontalLine: {
    // Account for padding of parent modal; this should stretch across the entire width of the modal body
    marginLeft: '-16px',
    marginRight: '-16px',
  },
});
